import { clamp } from 'lodash-es';

import { maxScale, minScale } from './scaleOptions';

interface PitchToZoomOptions {
  scale: number;
  onTouchMoveComplete: (
    nextScale: number,
    offset?: { clientX: number; clientY: number },
  ) => void;
}

export function createPitchToZoom(element: HTMLElement, options: PitchToZoomOptions) {
  let pinchStartDist = 0;
  let pinchStartScale = 0;

  const getDist = (touches: TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      pinchStartDist = getDist(e.touches);
      pinchStartScale = options.scale;
      e.preventDefault();
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDist > 0) {
      const ratio = Math.pow(getDist(e.touches) / pinchStartDist, 1.3);
      const next = clamp(pinchStartScale * ratio, minScale, maxScale);

      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

      e.preventDefault();
      options.onTouchMoveComplete(next, { clientX: centerX, clientY: centerY });
    }
  };

  const onTouchEnd = () => {
    pinchStartDist = 0;
  };

  const onWheel = (event: WheelEvent) => {
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.002);
      const next = clamp(options.scale * factor, minScale, maxScale);

      options.onTouchMoveComplete(next, {
        clientX: event.clientX,
        clientY: event.clientY,
      });
    }
  };

  const attachListeners = () => {
    if (!element) return false;
    element.addEventListener('touchstart', onTouchStart, { passive: false });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd);
    element.addEventListener('touchcancel', onTouchEnd);
    element.addEventListener('wheel', onWheel, { passive: false });
    element.style.touchAction = 'pan-x pan-y';
    return true;
  };

  const detachListeners = () => {
    if (!element) return false;
    element.removeEventListener('touchstart', onTouchStart);
    element.removeEventListener('touchmove', onTouchMove);
    element.removeEventListener('touchend', onTouchEnd);
    element.removeEventListener('touchcancel', onTouchEnd);
    element.removeEventListener('wheel', onWheel);
    return true;
  };

  return { attachListeners, detachListeners };
}
